import React from 'react';
import PropTypes from 'prop-types';
import styled, { ThemeProvider } from 'styled-components';
import { animated, useSpring } from 'react-spring';
import { useMediaQuery } from 'react-responsive';
import Avatar from '../shared/avatar';
import theme from '../../../assets/stylesheets/_settings.global.scss';

const Container = styled(animated.div)`
  z-index: 500;
  position: fixed;
  bottom: 28px;
  ${props => !props.theme.isMobile && `
    left: 25px;
  `};
`;

const Prompt = styled.div`
  height: ${props => props.height}px;
  width: ${props => (props.theme.isMobile ? 335 : 450)}px;
  background-color: ${props => props.theme.bodyBackgroundColor};
  border: 1px solid #d7dfe7;
  box-shadow: 0px 12px 12px rgba(50, 50, 71, 0.08), 0px 16px 24px rgba(50, 50, 71, 0.08);
  border-radius: 6px;
  padding: 6px 6px 24px 24px;
  ${props => props.theme.isMobile && `
    padding: 0px 0px 24px 16px;
  `};
  margin-top: -${props => props.height}px;
`;

const Profile = styled.div`
  display: flex;
  flex-direction: ${props => (props.extended ? 'column' : 'row')};
  margin-top: 18px;
  ${props => !props.extended && `
    align-items: center;
  `};


  & > a {
    margin-right: 16px;

    > img {
      width: 56px;
      height: 56px;
      margin-right: 0px;
    }
  }
`;

const Cross = styled.span`
  width: 28px;
  height: 28px;
  font-size: ${props => (props.theme.isMobile ? 28 : 36)}px;
  justify-content: flex-end;
  color: ${props => props.theme.secondaryColor};
  cursor: pointer;
  &:before {
    content: "\u00d7";
  }

  &:hover {
    color: ${props => props.theme.primaryColor};
  }
`;

const PromptFlex = styled.div`
  display: flex;
  justify-content: space-between;
`;

const ProfileFlex = styled.div`
  display: flex;
  justify-content: flex-start;
`;

const VFlex = styled.div`
  display: flex;
  flex-direction: column;
`;

const Name = styled.span`
  font: ${props => props.theme.fontWeightHeavy} 19px/110% ${props => props.theme.fontFamilyBody};
  color: ${props => props.theme.bodyColor};
  text-transform: uppercase;
  padding-top: 6px;
`;

const Company = styled.span`
  font: ${props => props.theme.fontWeightHeavy} 19px/110% ${props => props.theme.fontFamilyBody};
  color: ${props => props.theme.secondaryColor};
  margin-top: 5px;
`;

const Text = styled.span`
  font-weight: ${props => (props.extended ? props.theme.fontWeightRegular : props.theme.fontWeightHeavy)};
  font-size: 19px;
  font-family: ${props => props.theme.fontFamilyBody};
  color: ${props => props.theme.bodyColor};
  line-height: 120%;
  ${props => props.extended && `
    margin-top: 16px;
  `};
  ${props => !props.extended && props.theme.isMobile && `
    font-size: 15px;
    line-height: 140%;
  `};
  ${props => !props.extended && !props.theme.isMobile && `
    font-size: 19px;
  `};
`;

const Divider = styled.hr`
  height: 0px;
  border: 0;
  border-top: 1px solid ${props => props.color};
  border-radius: 2px;
  margin-top: 16px;
  margin-right: 18px;
`;

const Footer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-top: 16px;
  margin-right: 18px;
`;

const Wires = styled.span`
  font: ${props => props.theme.fontWeightBold} 16px/120% ${props => props.theme.fontFamilyBody};
  color: ${props => props.theme.secondaryColor};
  margin-right: 25px;
  ${props => !props.theme.isMobile && `
    padding-top: 8px;
  `};
`;

const Number = styled.span`
  color: ${props => props.theme.bodyColor};
  font-size: 19px;
`;

const Button = styled.button`
  width: 148px;
  height: 40px;
  font: ${props => props.theme.fontWeightHeavy} 14px/120% ${props => props.theme.fontFamilyBody};
  text-transform: uppercase;
  color: #ffffff;
  outline: none;
  background-color: ${props => props.color};
  border: 1px solid ${props => props.color};
  border-radius: 2px;
  padding: 11px 7px;
`;

export default function PromptToViewProfile(props) {
  const isMobile = useMediaQuery({ query: `(max-width: ${theme.breakpointMediumMax})` });
  const [showPrompt, setShowPrompt] = React.useState(false);
  const { profile, type, timeout } = props;
  const { firstName, totalWires } = profile;
  const isWide = type === 'wide';
  const height = isWide ? 240 : 176;
  const dividerColor = isWide ? '#8ea3b5' : '#c19d3f';
  const buttonColor = isWide ? '#c19d3f' : '#3d4a63';
  const text = `Learn more about ${firstName}’s expertise and insights on their Livewire profile.`;
  const slide = useSpring({
    transform: showPrompt ? 'translateX(0px)' : 'translateX(-500px)',
  });
  const plural = totalWires > 1 ? 's' : '';

  React.useEffect(() => {
    setTimeout(() => setShowPrompt(true), timeout);
  }, [timeout]);

  const viewProfile = e => {
    e && e.preventDefault();
    window.location = `/contributors/${profile.slug}?exp=wire_profile_view_prompt`;
  };

  const close = e => {
    e && e.preventDefault();
    setShowPrompt(false);
  };

  return (
    <ThemeProvider theme={{ ...theme, isMobile }}>
      <Container style={slide}>
        <Prompt height={height}>
          <PromptFlex>
            {!!isWide && (
              <Profile extended>
                <ProfileFlex>
                  <Avatar {...profile} />
                  <VFlex>
                    <Name>{profile.name}</Name>
                    <Company>{profile.company}</Company>
                  </VFlex>
                </ProfileFlex>
                <Text extended>{text}</Text>
              </Profile>
            )}
            {!isWide && (
              <Profile>
                <Avatar {...profile} />
                <Text>{text}</Text>
              </Profile>
            )}
            <Cross onClick={close} />
          </PromptFlex>
          <Divider color={dividerColor} />
          <Footer>
            <Wires>
              Published/Mentioned in
              <Number>{` ${totalWires} `}</Number>
              {`wire${plural}`}
            </Wires>
            <Button color={buttonColor} onClick={viewProfile}>view profile</Button>
          </Footer>
        </Prompt>
      </Container>
    </ThemeProvider>
  );
}

PromptToViewProfile.propTypes = {
  profile: PropTypes.shape({
    profilePictureUrl: PropTypes.string,
    firstName: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    slug: PropTypes.string.isRequired,
    company: PropTypes.string.isRequired,
    totalWires: PropTypes.number.isRequired,
  }).isRequired,
  type: PropTypes.string.isRequired,
  timeout: PropTypes.number.isRequired,
};
